import type {
  ChecklistAnswer,
  ChecklistAnswerInput,
  ChecklistAssignment,
  ChecklistCategory,
  ChecklistIssue,
  ChecklistItemDef,
  ChecklistRecipient,
  ChecklistResult,
  ChecklistTemplate,
  SiteShift,
  Alarm,
  AlarmNotification,
  AlarmStatus,
  AlarmType,
  AlertHours,
  SiteAlertSettings,
  SiteNightContact,
  CurrentUser,
  DbError,
  Device,
  Geofence,
  GeofenceKind,
  GeofenceShapeType,
  LonLat,
  PushSubscriptionInput,
  Invitation,
  InvitationPreview,
  CardStatus,
  CardTech,
  DocumentKind,
  LiveSnapshot,
  Operator,
  OperatorCard,
  OperatorDocument,
  OperatorPermission,
  PowerType,
  RegisteredDevice,
  Site,
  SiteKind,
  Tenant,
  TenantRole,
  Vehicle,
  VehicleType,
} from "@fts/core";
import type { ModuleSettingsInput, VehicleModuleData } from "./modules";
import type { ReportsApi } from "./reports";

/** A tenant member as shown on the Users & roles screen. */
export type Member = {
  userId: string;
  email: string;
  displayName: string;
  role: TenantRole;
  /** Empty = access to all sites of the tenant. */
  siteIds: string[];
};

/** Error codes of the Worker API (apps/worker) besides the DbError tokens. */
export const API_ERROR_CODES = ["unauthorized", "bad_request", "not_found", "broker_error", "mail_error", "push_disabled", "unknown"] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Error codes the UI knows how to translate. */
/** "network": the request did not reach the server (offline, DNS, captive portal). */
/** "captcha_failed": Supabase Auth rejected the Turnstile token; "invalid_code": wrong or expired e-mail code (ADR 0020). */
export type DataErrorCode = "invalid_credentials" | "not_configured" | "network" | "captcha_failed" | "invalid_code" | AuthErrorCode | ApiErrorCode | DbError;

/**
 * Password change errors (Supabase Auth). "reauthentication_needed": the session is too old or gone;
 * the user signs out and back in with an e-mail link first. "not_in_demo": demo accounts cannot change it.
 */
export const AUTH_ERROR_CODES = ["weak_password", "same_password", "reauthentication_needed", "not_in_demo"] as const;
export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number];

/** Sign-in extras (ADR 0020). `captchaToken`: Cloudflare Turnstile token; `remember: false` keeps the session for this browser session only. */
export type SignInOptions = { captchaToken?: string; remember?: boolean };

/** Create when `id` is absent, update otherwise. Tenant and parent rules are enforced by RLS. */
export type SiteInput = {
  id?: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  kind: SiteKind;
  address: string | null;
  lat: number | null;
  lon: number | null;
};

export type VehicleInput = {
  id?: string;
  tenantId: string;
  siteId: string;
  fleetNo: string;
  type: VehicleType;
  power: PowerType;
  make: string | null;
  model: string | null;
  serialNo: string | null;
  batteryVoltage: number | null;
  active: boolean;
  /** "Start without card" alarm switch (fleet manager and above, like the rest of the form). */
  unauthorizedStartAlarm: boolean;
};

export type DeviceRegistration = { deviceId: string; imei?: string | null; iccid?: string | null; hw?: string | null };

/**
 * A freshly registered device. `brokerSynced: false` means the database has the device but the
 * managed MQTT broker could not be updated, so the secret will not work until it is rotated.
 * Absent = no separate broker to sync (demo, embedded broker).
 */
export type RegistrationResult = RegisteredDevice & { brokerSynced?: boolean };

/** Result of a secret rotation; see RegistrationResult for `brokerSynced`. */
export type RotationResult = { secret: string; brokerSynced: boolean };

/**
 * Result of blocking/unblocking. When the broker forgets a blocked device's credentials
 * (EMQX Serverless), unblocking issues a NEW secret that must be shown once.
 */
export type RevocationResult = { secret?: string; brokerSynced: boolean };

export type InvitationInput = { tenantId: string; email: string; role: TenantRole; siteIds: string[] };

/* ---- Sprint 5: operators ---- */

/** Everything operator-related the user may see in a tenant. Cards and documents stay empty for viewers (RLS). */
export type OperatorData = {
  operators: Operator[];
  cards: OperatorCard[];
  documents: OperatorDocument[];
  permissions: OperatorPermission[];
};

export type OperatorInput = {
  id?: string;
  tenantId: string;
  siteId: string | null;
  fullName: string;
  employeeNo: string | null;
  active: boolean;
  leftOn: string | null;
};

export type CardInput = { tenantId: string; operatorId: string; uid: string; tech: CardTech };

export type DocumentInput = {
  id?: string;
  tenantId: string;
  operatorId: string;
  kind: DocumentKind;
  label: string | null;
  validUntil: string;
  required: boolean;
};

/** Exactly one of siteId / vehicleId; vehicleType only with a site. */
export type PermissionInput = {
  tenantId: string;
  operatorId: string;
  siteId: string | null;
  vehicleType: VehicleType | null;
  vehicleId: string | null;
  validUntil: string | null;
};

/** Delivery state of a device's authorisation list (no card UIDs). */
export type AuthlistStatus = {
  deviceId: string;
  ver: number;
  changedAt: string | null;
  sentVer: number | null;
  sentAt: string | null;
  ackedVer: number | null;
  ackedAt: string | null;
  error: string | null;
};

/** Who is on a vehicle right now (by device), for the live map. Card numbers are never sent to the browser. */
export type LiveOperator = { deviceId: string; operatorId: string; fullName: string };

/**
 * A running "live watch" (ADR 0014): the vehicle's device reports every second until `until`.
 * `acked` = the device confirmed the command; until then the console shows "waiting for the device".
 */
export type LiveWatch = { vehicleId: string; deviceId: string; until: string; acked: boolean };

/* ---- Sprint 6a: alarms and notification settings (ADR 0011) ---- */

/** Server-side filters of the alarm list. `from`/`to` are ISO instants ([from, to)). */
export type AlarmQuery = {
  status?: AlarmStatus | null;
  type?: AlarmType | null;
  /** Sites to include (the caller expands a site to its sub-sites). */
  siteIds?: string[] | null;
  vehicleId?: string | null;
  from?: string | null;
  to?: string | null;
  /** Default 500, newest first. */
  limit?: number;
};

/**
 * A console user as far as the signed-in user may see them (RLS on memberships/profiles):
 * tenant admins see everyone, other roles only themselves. Used for names of notification
 * recipients, night contacts and the night-contact picker.
 */
export type AlertPerson = { userId: string; displayName: string; role: TenantRole; siteIds: string[] };

/** Working hours that apply to a site; `sourceSiteId` null = system defaults. */
export type EffectiveAlertSettings = { hours: AlertHours; sourceSiteId: string | null };

/** A possible night contact of a site (a member who can see it). */
export type ContactCandidate = { userId: string; displayName: string; role: TenantRole };

/** A person named on a screen: id and display name only. */
export type PersonName = { userId: string; displayName: string };

/**
 * Night contacts that apply to a site, resolved on the server like the notification trigger.
 * `sourceSiteId`: the site they are defined on (may be a parent the user cannot see; only its id
 * is known); null with an empty list = none anywhere (off-hours impacts go to the day recipients).
 */
export type EffectiveNightContacts = { contacts: PersonName[]; sourceSiteId: string | null };

/**
 * `site_alert_recipients_status(site)`: how many people an alarm on the site would reach by e-mail right
 * now (counts only, resolved like the notification trigger). `nightRecipients` equals `dayRecipients` when
 * no night contact applies; `adminsFallback`: no safety officer or fleet manager with an e-mail sees the
 * site, so the day recipients are the company admins (if any); `adminCopies`: admins with an e-mail who see
 * the site and get a copy of every alarm because of tenants.alarm_copy_admins (0 when the setting is off).
 */
export type AlertRecipientsStatus = { dayRecipients: number; nightRecipients: number; adminsFallback: boolean; adminCopies: number };

/** Upsert of a site's working hours (time zone stays Europe/Istanbul). */
export type AlertSettingsInput = { tenantId: string; siteId: string; workDays: number[]; workStart: string; workEnd: string };

export type UiLocale = "tr" | "en";

/* ---- Sprint 7b: web push (ADR 0019) ---- */
/** GET /api/push/config: enabled = the server has VAPID keys. */
export type PushConfig = { enabled: boolean; publicKey: string | null };
/** One of the signed-in user's push devices (push_subscriptions; the keys are never read back). */
export type PushDevice = {
  id: string;
  endpoint: string;
  label: string | null;
  createdAt: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  failureCount: number;
  lastError: string | null;
};
export type PushTestResult = { sent: number; failed: number; removed: number };

/* ---- Sprint 6b: geofences (ADR 0015) ---- */

/**
 * Create when `id` is absent, update otherwise (the site of an existing zone does not change).
 * Fields of the other shape and `dwellLimitS` of non-dwell zones are sent as null.
 */
export type GeofenceInput = {
  id?: string;
  tenantId: string;
  siteId: string;
  name: string;
  kind: GeofenceKind;
  shape: GeofenceShapeType;
  coords: LonLat[] | null;
  centerLat: number | null;
  centerLon: number | null;
  radiusM: number | null;
  /** Empty = every vehicle type. */
  vehicleTypes: VehicleType[];
  dwellLimitS: number | null;
  active: boolean;
};

/* ---- Sprint 7a: pre-shift checklist (ADR 0016) ---- */

/** One item of the public form (critical flags are never sent to the form: the operator gets no warning). */
/** `naAllowed`: whether "Yok" may be chosen (critical items only with allow_na); the critical flag itself is not sent. */
export type ChecklistFormItem = { id: string; code: string; textTr: string; textEn: string; photoRequiredOnFail: boolean; naAllowed: boolean };

/** What the QR form shows (checklist_form). No operator identity (KVKK). */
export type ChecklistForm = {
  vehicle: { fleetNo: string; type: VehicleType; power: PowerType; siteName: string | null };
  template: { id: string; name: string; nameEn: string } | null;
  items: ChecklistFormItem[];
  /** The vehicle had an impact alarm since its last checklist: the body-damage item comes first. */
  impactSinceLast: boolean;
  /** Open faults of the vehicle (shown at the top of the form). */
  openFaults: { code: string; textTr: string; textEn: string; createdAt: string }[];
  shift: { name: string; startsAt: string; endsAt: string } | null;
  submittedThisShift: boolean;
  /** Template version the form was built from; sent back so a changed template returns form_changed. */
  version: string;
};

/** `clientSubmissionId`: one random UUID per form load; a retry with the same key returns the same submission. */
export type ChecklistSubmitInput = { locale: UiLocale; answers: ChecklistAnswerInput[]; clientSubmissionId: string; formVersion?: string };
/** The form never learns the result (the operator gets no warning, ADR 0013). */
export type ChecklistSubmitResult = { submissionId: string };

/** checklist_submit refused the answers; `issues` say which item and why (same codes as core). */
export class ChecklistInvalidError extends Error {
  readonly code = "invalid_checklist" as const;
  constructor(readonly issues: ChecklistIssue[]) {
    super("invalid_checklist");
    this.name = "ChecklistInvalidError";
  }
}

/** Create when `id` is absent, update otherwise (the site of a shift does not change). */
export type ShiftInput = { id?: string; tenantId: string; siteId: string; name: string; start: string; end: string; weekdays: number[]; tz: string };

/** Exactly one of userId (fleet manager+) / email (tenant admin). */
export type ChecklistRecipientInput = { tenantId: string; siteId: string; userId: string | null; email: string | null; label: string | null };

/**
 * effective_checklist_recipients(site): resolved like the notification trigger. Members and external
 * addresses resolve separately (each row carries its source site). External addresses and their labels
 * only for fleet managers+ of the site. `sourceSiteId` = the members' source; null = no member recipient
 * anywhere (the day recipients get the alarms). `externalSourceSiteId` likewise for external addresses.
 */
export type EffectiveChecklistRecipients = {
  recipients: { recipientId: string; userId: string | null; displayName: string | null; email: string | null; sourceSiteId: string }[];
  sourceSiteId: string | null;
  externalSourceSiteId: string | null;
};

export type ChecklistSubmission = {
  id: string;
  tenantId: string;
  siteId: string | null;
  vehicleId: string;
  templateName: string;
  category: ChecklistCategory;
  shiftName: string | null;
  shiftStart: string | null;
  submittedAt: string;
  operatorId: string | null;
  operatorUnknown: boolean;
  result: ChecklistResult;
  impactSinceLast: boolean;
};

/** A stored answer (item text and flags are snapshots taken at submission). */
export type ChecklistAnswerRow = {
  id: string;
  submissionId: string;
  position: number;
  code: string;
  textTr: string;
  textEn: string;
  critical: boolean;
  photoRequiredOnFail: boolean;
  answer: ChecklistAnswer;
  note: string | null;
};

export type ChecklistPhoto = { id: string; submissionId: string | null; answerId: string | null; path: string; contentType: string };

export type ChecklistSubmissionDetail = { submission: ChecklistSubmission; answers: ChecklistAnswerRow[]; photos: ChecklistPhoto[] };

export type ChecklistFault = {
  id: string;
  tenantId: string;
  siteId: string | null;
  vehicleId: string;
  submissionId: string;
  answerId: string;
  itemCode: string;
  textTr: string;
  textEn: string;
  critical: boolean;
  note: string | null;
  status: "open" | "resolved";
  createdAt: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
};

export type FaultQuery = { status?: "open" | "resolved" | null; vehicleId?: string | null; limit?: number };
export type SubmissionQuery = { vehicleId?: string | null; limit?: number };

/** Items in form order; `id` keeps an existing item (its history links), absent = new item. */
export type ChecklistTemplateInput = { id: string; name: string; items: (ChecklistItemDef & { id?: string })[] };

/** QR token of a vehicle (fleet managers+): the label URL is `${origin}/#/c/${token}`. */
export type ChecklistToken = { vehicleId: string; token: string; rotatedAt: string | null };

/** Realtime connection state of the live map feed. */
export type LiveFeedStatus = "connecting" | "live" | "reconnecting";

/** Stop a live subscription. */
export type Unsubscribe = () => void;

export class DataError extends Error {
  constructor(
    public readonly code: DataErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "DataError";
  }
}

/**
 * Everything the console reads and writes. The demo implementation keeps fictional sample data in
 * memory; the Supabase implementation relies on RLS and the SECURITY DEFINER functions of the
 * devices/invitations migration. Writes throw DataError with a DbError code on rule violations.
 */
export interface DataProvider extends ReportsApi {
  /** "demo" providers make the UI show a sample-data badge. */
  readonly mode: "demo" | "supabase";
  getCurrentUser(): Promise<CurrentUser | null>;
  signIn(email: string, password: string, options?: SignInOptions): Promise<CurrentUser>;
  /**
   * E-mail code sign-in, step 1: e-mails a 6-digit code (and a sign-in link) to an EXISTING account.
   * Resolves without error for unknown addresses so the form does not reveal who has an account.
   */
  sendSignInCode(email: string, options?: SignInOptions): Promise<void>;
  /** False when the current session ends with the browser ("Remember me" unticked; ADR 0020). */
  sessionRemembered(): boolean;
  /** E-mail code sign-in, step 2. Throws DataError("invalid_code") for a wrong or expired code. */
  verifySignInCode(email: string, code: string, options?: Pick<SignInOptions, "remember">): Promise<CurrentUser>;
  signInDemo(): Promise<CurrentUser>;
  signOut(): Promise<void>;
  listTenants(): Promise<Tenant[]>;
  listSites(tenantId: string): Promise<Site[]>;
  listVehicles(tenantId: string): Promise<Vehicle[]>;
  listMembers(tenantId: string): Promise<Member[]>;

  /* ---- Sprint 2: fleet master data ---- */
  /** Platform admin only. */
  createTenant(input: { name: string; slug: string }): Promise<Tenant>;
  saveSite(input: SiteInput): Promise<Site>;
  deleteSite(siteId: string): Promise<void>;
  saveVehicle(input: VehicleInput): Promise<Vehicle>;

  /* ---- Sprint 2: devices ---- */
  /** Devices of one tenant visible to the user (paired ones on visible vehicles; unpaired for fleet managers). */
  listDevices(tenantId: string): Promise<Device[]>;
  /** Platform admin: every device, including unclaimed inventory. */
  listInventory(): Promise<Device[]>;
  /** Platform admin: register devices; secrets and claim codes are returned only here. */
  registerDevices(input: DeviceRegistration[]): Promise<RegistrationResult[]>;
  /** Platform admin: new device secret (old one stops working). */
  rotateDeviceSecret(deviceId: string): Promise<RotationResult>;
  /** Platform admin: block or unblock a device at the broker and in ingest. */
  setDeviceRevoked(deviceId: string, revoked: boolean): Promise<RevocationResult>;
  /** Platform admin: take a device back from a customer; returns a fresh claim code. */
  releaseDevice(deviceId: string): Promise<{ claimCode: string; brokerSynced: boolean }>;
  /** Fleet manager+: add a device to the tenant with the code on its label. */
  claimDevice(tenantId: string, deviceId: string, claimCode: string): Promise<void>;
  /** Fleet manager+ on the vehicle's site. One device per vehicle. */
  pairDevice(deviceId: string, vehicleId: string): Promise<void>;
  unpairDevice(deviceId: string): Promise<void>;

  /* ---- Sprint 2: members and invitations ---- */
  updateMemberRole(tenantId: string, userId: string, role: TenantRole): Promise<void>;
  removeMember(tenantId: string, userId: string): Promise<void>;
  listInvitations(tenantId: string): Promise<Invitation[]>;
  /** The token is shown once; the invite link is `${origin}/#/invite/${token}`. */
  createInvitation(input: InvitationInput): Promise<{ invitation: Invitation; token: string }>;
  revokeInvitation(invitationId: string): Promise<void>;
  /**
   * E-mails the invitation link (token from createInvitation). Throws DataError("rate_limited")
   * after 5 sends or within 60 s of the previous send.
   */
  sendInvitationEmail(invitationId: string, token: string): Promise<void>;
  /** Works signed out. null = unknown token. */
  previewInvitation(token: string): Promise<InvitationPreview | null>;
  /** Signed-in user whose verified e-mail matches the invitation. Returns the tenant id. */
  acceptInvitation(token: string): Promise<string>;
  /** Passwordless sign-in: e-mails a one-time link that returns to `redirectTo`. */
  sendSignInLink(email: string, redirectTo: string, options?: Pick<SignInOptions, "captchaToken">): Promise<void>;
  updateDisplayName(displayName: string): Promise<void>;
  /**
   * Set or change the signed-in user's console password (also for accounts that only used e-mail links).
   * Throws DataError("weak_password" | "same_password" | "reauthentication_needed" | "rate_limited" | "network");
   * the demo throws "not_in_demo". The console never logs or stores the password.
   */
  changePassword(newPassword: string): Promise<void>;

  /* ---- Sprint 4: live map ---- */
  /** Latest state of the devices on the tenant's vehicles the user can see (RLS: current pairing only). */
  listLive(tenantId: string): Promise<LiveSnapshot[]>;
  /**
   * Push every change of a latest-state row the user may see (Supabase Realtime, RLS applies).
   * The caller filters by tenant. Returns a function that stops the subscription.
   */
  subscribeLive(onChange: (s: LiveSnapshot) => void, onStatus?: (s: LiveFeedStatus) => void): Unsubscribe;

  /* ---- Sprint 7a: battery and lift modules (ICD v0.2 section 9, ADR 0017) ---- */
  /** Module data of one vehicle the user can see (RLS); empty lists when the vehicle has no modules. */
  getVehicleModuleData(vehicleId: string): Promise<VehicleModuleData>;
  /**
   * Module settings of the vehicle (fleet manager and above, vehicles policies). bat_type, bat_ah,
   * lift_kg_per_bar and lift_bar_threshold are then pushed to the device by config (API Worker, within a minute).
   */
  saveModuleSettings(input: ModuleSettingsInput): Promise<void>;

  /* ---- Sprint 5: operators, cards, documents, permissions (writes: safety and above) ---- */
  listOperatorData(tenantId: string): Promise<OperatorData>;
  saveOperator(input: OperatorInput): Promise<Operator>;
  addCard(input: CardInput): Promise<OperatorCard>;
  /** Lost and revoked are final for that card (a found card is added again as a new card). */
  setCardStatus(cardId: string, status: Exclude<CardStatus, "active">): Promise<void>;
  saveDocument(input: DocumentInput): Promise<OperatorDocument>;
  deleteDocument(documentId: string): Promise<void>;
  addPermission(input: PermissionInput): Promise<OperatorPermission>;
  deletePermission(permissionId: string): Promise<void>;
  /** Sync state of the tenant's devices the user can see. */
  listAuthlistStatus(tenantId: string): Promise<AuthlistStatus[]>;
  /** Ask the API to push changed lists now (otherwise within a minute). */
  syncAuthlists(tenantId: string): Promise<{ sent: number; failed: number }>;
  listLiveOperators(tenantId: string): Promise<LiveOperator[]>;

  /* ---- ADR 0014: live watch (safety and above on the vehicle's site) ---- */
  /**
   * Ask the vehicle's device to report every second for `durS` seconds (default 600, 60..1800).
   * Throws DataError("rate_limited") within 30 s of the previous request for the same device,
   * "device_offline" when the device is not connected, "too_many_live_watches" over 5 per tenant.
   * Cadence only: never affects the ignition (ADR 0003).
   */
  startLiveWatch(vehicleId: string, durS?: number): Promise<LiveWatch>;
  /** Running live watches (sent to the broker) on vehicles the user can see. */
  listLiveWatches(tenantId: string): Promise<LiveWatch[]>;

  /* ---- Sprint 6a: alarms (read: anyone who sees the site; acknowledge: safety+ on the site) ---- */
  listAlarms(tenantId: string, query?: AlarmQuery): Promise<Alarm[]>;
  /** null when the alarm does not exist or the user may not see it. */
  getAlarm(tenantId: string, alarmId: string): Promise<Alarm | null>;
  /** Open alarms the user may see (rail and bell badge). */
  countOpenAlarms(tenantId: string): Promise<number>;
  /** Notification rows of an alarm the user may see (own rows; all rows of the site for safety+). */
  listAlarmNotifications(alarmId: string): Promise<AlarmNotification[]>;
  /** `acknowledge_alarm(id, note)`; final (cannot be undone). Note ≤ 500 characters. */
  acknowledgeAlarm(alarmId: string, note: string | null): Promise<void>;
  /** `alarm_people(id)`: names of the alarm's notification recipients and acknowledger (empty without access). */
  listAlarmPeople(alarmId: string): Promise<PersonName[]>;

  /* ---- Sprint 6a: working hours and night contacts (write: fleet manager+ on the site) ---- */
  listAlertSettings(tenantId: string): Promise<SiteAlertSettings[]>;
  /**
   * `effective_alert_settings(site)`: own row, else nearest ancestor's (even one the user cannot see),
   * else the defaults. null when the user cannot see the site.
   */
  getEffectiveAlertSettings(siteId: string): Promise<EffectiveAlertSettings | null>;
  /** `alert_contact_candidates(site)`: members who see the site (fleet manager+ on the site, else forbidden). */
  listContactCandidates(siteId: string): Promise<ContactCandidate[]>;
  saveAlertSettings(input: AlertSettingsInput): Promise<SiteAlertSettings>;
  /** Delete the site's own row so it inherits again. */
  resetAlertSettings(siteId: string): Promise<void>;
  listNightContacts(tenantId: string): Promise<SiteNightContact[]>;
  /** `set_night_contacts(site, ids)`: replace the site's own night contacts atomically; empty = inherit from the parent site. */
  setNightContacts(tenantId: string, siteId: string, userIds: string[]): Promise<void>;
  /** `effective_night_contacts(site)`: also through parent sites the user cannot see; empty when the user cannot see the site. */
  getEffectiveNightContacts(siteId: string): Promise<EffectiveNightContacts>;
  /** `site_alert_recipients_status(site)`: counts only; null when the user cannot see the site. */
  getAlertRecipientsStatus(siteId: string): Promise<AlertRecipientsStatus | null>;
  listAlertPeople(tenantId: string): Promise<AlertPerson[]>;
  /**
   * tenants.alarm_copy_admins (ADR 0011): admins receive a copy of every alarm e-mail. Readable by every
   * member of the tenant; false when unknown.
   */
  getAlarmCopyAdmins(tenantId: string): Promise<boolean>;
  /** `set_tenant_alarm_copy_admins(tenant, on)`: tenant admins and platform admins only (else DataError("forbidden")). */
  setAlarmCopyAdmins(tenantId: string, on: boolean): Promise<void>;
  /** profiles.locale: language of the user's e-mails. */
  updateLocale(locale: UiLocale): Promise<void>;

  /* ---- Sprint 7b: web push (ADR 0019; every user manages only their own devices) ---- */
  getPushConfig(): Promise<PushConfig>;
  listPushDevices(): Promise<PushDevice[]>;
  /** push_subscribe(): registers this browser for the signed-in user; returns the device id. */
  savePushSubscription(input: PushSubscriptionInput): Promise<string>;
  removePushDevice(id: string): Promise<void>;
  /** A test notification to all of the user's devices (Worker; at most one per 30 s). */
  sendTestPush(): Promise<PushTestResult>;

  /* ---- Sprint 6b: geofences (read: anyone who sees the site; write: safety, fleet manager, admin on the site) ---- */
  /**
   * Zones the user can read, active and inactive: those of the sites they see plus, for a user scoped to
   * some sites, the zones of those sites' ancestors (they apply to the user's vehicles). `siteId` limits
   * the query to the zones defined on that one site.
   */
  listGeofences(tenantId: string, siteId?: string): Promise<Geofence[]>;
  /** null when the zone does not exist (deleted) or the user may not see it. */

  getGeofence(geofenceId: string): Promise<Geofence | null>;
  /** Throws DataError("invalid_geofence") when the zone breaks a rule of validateGeofence. */
  saveGeofence(input: GeofenceInput): Promise<Geofence>;
  deleteGeofence(geofenceId: string): Promise<void>;

  /* ---- Sprint 7a: pre-shift checklist (ADR 0016) ---- */
  /** Public form by QR token (no sign-in). Throws DataError("checklist_token_invalid" | "rate_limited"). */
  getChecklistForm(token: string): Promise<ChecklistForm>;
  /** Upload one photo (jpeg/png/webp, ≤ 5 MB) for the form; returns its id. DataError("invalid_photo"). */
  uploadChecklistPhoto(token: string, file: Blob): Promise<string>;
  /** Throws ChecklistInvalidError with the issues, or DataError. Never locks the vehicle (ADR 0003). */
  submitChecklist(token: string, input: ChecklistSubmitInput): Promise<ChecklistSubmitResult>;

  /** Shifts of the sites the user can see. Write: fleet manager+ on the site. */
  listShifts(tenantId: string): Promise<SiteShift[]>;
  saveShift(input: ShiftInput): Promise<SiteShift>;
  deleteShift(shiftId: string): Promise<void>;

  /** Checklist alarm recipients the user may see (external addresses: fleet manager+ only). */
  listChecklistRecipients(tenantId: string): Promise<ChecklistRecipient[]>;
  addChecklistRecipient(input: ChecklistRecipientInput): Promise<ChecklistRecipient>;
  deleteChecklistRecipient(recipientId: string): Promise<void>;
  getEffectiveChecklistRecipients(siteId: string): Promise<EffectiveChecklistRecipients>;

  /** System templates and the tenant's own, with items in form order. */
  listChecklistTemplates(tenantId: string): Promise<ChecklistTemplate[]>;
  /** Fleet managers without a site scope and admins. Returns the new template's id. */
  copyChecklistTemplate(tenantId: string, templateId: string, name: string | null): Promise<string>;
  saveChecklistTemplate(input: ChecklistTemplateInput): Promise<void>;
  deleteChecklistTemplate(templateId: string): Promise<void>;
  listChecklistAssignments(tenantId: string): Promise<ChecklistAssignment[]>;
  /** null = back to the system template of the category. */
  setChecklistAssignment(tenantId: string, category: ChecklistCategory, templateId: string | null): Promise<void>;

  listChecklistSubmissions(tenantId: string, query?: SubmissionQuery): Promise<ChecklistSubmission[]>;
  getChecklistSubmission(submissionId: string): Promise<ChecklistSubmissionDetail | null>;
  listChecklistFaults(tenantId: string, query?: FaultQuery): Promise<ChecklistFault[]>;
  /** Fleet manager+ on the fault's site; once. */
  resolveChecklistFault(faultId: string, note: string | null): Promise<void>;
  /** A short-lived URL to show a photo the user may see. */
  checklistPhotoUrl(path: string): Promise<string>;
  listChecklistTokens(tenantId: string): Promise<ChecklistToken[]>;
  /** Tenant admins: the printed label stops working. */
  rotateChecklistToken(vehicleId: string): Promise<string>;
}
